/**
 * These helper functions fixes disappearing embed content (HubSpot, Typeform, Calendly) on SPA remounts.
 * Loads scripts once via JS and re-renders embed containers on each mount.
 */

function loadScriptOnce({ id, src }, onReady) {
  if (typeof window === "undefined") return

  // If the script tag already exists, wait for its load event and then callback.
  const existing = document.getElementById(id)
  if (existing) {
    existing.addEventListener("load", onReady, { once: true })
    if (onReady) onReady()
    return
  }

  // If script does not exist, create and append the script tag to load the library
  const script = document.createElement("script")
  script.id = id
  script.src = src
  script.async = true
  script.onload = onReady
  document.body.appendChild(script)
}

// Configuration for each third‑party embed service
const embedServices = [
  {
    name: "hubspot",
    detectScript: (src = "") => /js\.hsforms\.net\/forms\/embed/i.test(src),
    loader: { id: "hs-forms-v2", src: "https://js.hsforms.net/forms/v2.js" },
    isReady: () => !!window.hbspt?.forms?.create,
    findContainers: root =>
      root.querySelectorAll(
        "div[data-form-id][data-portal-id]:not([data-hs-rendered])"
      ),
    render: nodes =>
      nodes.forEach(c => {
        const { region = "na1", portalId, formId } = c.dataset
        if (!portalId || !formId) return
        window.hbspt.forms.create({
          region,
          portalId,
          formId,
          target: `div[data-form-id='${formId}']`,
        })
        c.setAttribute("data-hs-rendered", "true")
      }),
  },
  {
    name: "typeform",
    detectScript: (src = "") =>
      /embed\.typeform\.com\/(next\/)?bundle\.js/i.test(src),
    loader: {
      id: "typeform-embed",
      src: "https://embed.typeform.com/embed.js",
    },
    isReady: () => !!window.typeformEmbed?.makeWidget,
    findContainers: root =>
      root.querySelectorAll("div[data-tf-widget]:not([data-tf-rendered])"),
    render: nodes =>
      nodes.forEach(c => {
        let url = c.dataset.tfWidget
        if (!url) return
        // Accept raw ID or full URL
        if (!/^https?:\/\//.test(url)) {
          url = `https://form.typeform.com/to/${url}`
        }
        window.typeformEmbed.makeWidget(c, url, {
          hideFooter: true,
          hideHeaders: true,
        })
        c.setAttribute("data-tf-rendered", "true")
      }),
  },
  {
    name: "calendly",
    detectScript: (src = "") =>
      /assets\.calendly\.com\/assets\/external\/widget\.js/i.test(src),
    loader: {
      id: "calendly-embed",
      src: "https://assets.calendly.com/assets/external/widget.js",
    },
    isReady: () => !!window.Calendly?.initInlineWidget,
    findContainers: root =>
      root.querySelectorAll(
        "div.calendly-inline-widget:not([data-calendly-rendered])"
      ),
    render: nodes =>
      nodes.forEach(c => {
        const url = c.dataset.url || c.getAttribute("data-url")
        if (!url) return
        window.Calendly.initInlineWidget({ url, parentElement: c })
        c.setAttribute("data-calendly-rendered", "true")
      }),
  },
]

// Prevent inline execution of embed scripts. they are managed via loadScriptOnce.
export const isEmbedScript = (src = "") =>
  embedServices.some(svc => svc.detectScript(src))

// Scan the `root` element for uninitialized embed containers,
// load or re-use each service's script, and render found nodes
export const renderAllEmbeds = root => {
  if (typeof window === "undefined" || !root) return

  embedServices.forEach(service => {
    const nodes = service.findContainers(root)
    if (!nodes.length) return

    const renderNow = () => service.isReady() && service.render(nodes)

    // If service is loaded, render immediately; otherwise load then render
    if (service.isReady()) renderNow()
    else loadScriptOnce(service.loader, renderNow)
  })
}
